import { getAccessToken } from "../../utils/auth";
import React, { useState, useEffect, useCallback } from "react";
import axios from "../../utils/api";
import { getRobotsGet } from "../../services/robotApi";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { IconButton } from "@mui/material";

const SOPSubmissionForm = () => {
  const [sopTypes, setSopTypes] = useState([]);
  const [robots, setRobots] = useState([]);
  const [robotsLoading, setRobotsLoading] = useState(true);
  const [selectedSop, setSelectedSop] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm();

  const sopType = watch("sopType");
  const selectedRobotId = watch("robotId");
  const watchedSections = watch("sections");

  // Initialize form with template data
  const initializeForm = useCallback(
    (template) => {
      const defaultValues = {
        robotId: "",
        location: "",
        date_time: "",
        message: "",
        sections: {},
      };

      if (template?.sections) {
        Object.keys(template.sections).forEach((sectionKey) => {
          const section = template.sections[sectionKey];
          defaultValues.sections[sectionKey] = {
            id: section.id,
            taskItems: {},
          };

          section.taskItems.forEach((task) => {
            defaultValues.sections[sectionKey].taskItems[task.id] = {
              id: task.id,
              completed: false,
              comment: "",
            };
          });
        });
      }

      setValue("robotId", defaultValues.robotId);
      setValue("location", defaultValues.location);
      setValue("date_time", defaultValues.date_time);
      setValue("message", defaultValues.message);

      if (defaultValues.sections) {
        Object.keys(defaultValues.sections).forEach((sectionKey) => {
          const section = defaultValues.sections[sectionKey];
          setValue(`sections.${sectionKey}.id`, section.id);

          Object.keys(section.taskItems).forEach((taskId) => {
            const task = section.taskItems[taskId];
            setValue(`sections.${sectionKey}.taskItems.${taskId}.id`, task.id);
            setValue(
              `sections.${sectionKey}.taskItems.${taskId}.completed`,
              task.completed
            );
            setValue(
              `sections.${sectionKey}.taskItems.${taskId}.comment`,
              task.comment
            );
          });
        });
      }
    },
    [setValue]
  );

  // Fetch robot list on mount
  useEffect(() => {
    const fetchRobots = async () => {
      try {
        setRobotsLoading(true);
        const response = await getRobotsGet();
        setRobots(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.error("Failed to fetch robots:", err);
        setError("Failed to load robot list");
      } finally {
        setRobotsLoading(false);
      }
    };
    fetchRobots();
  }, []);

  // Auto-fill location when a robot is selected
  useEffect(() => {
    if (!selectedRobotId || robots.length === 0) return;
    const robot = robots.find((r) => r.robotId === selectedRobotId);
    if (!robot) return;
    const locationText = [robot.location, robot.subLocation]
      .filter(Boolean)
      .join(" - ");
    setValue("location", locationText, { shouldValidate: Boolean(locationText) });
  }, [selectedRobotId, robots, setValue]);

  // Fetch SOP types on component mount
  useEffect(() => {
    const fetchSopTypes = async () => {
      try {
        const response = await axios.get("/sop/types");
        setSopTypes(response.data.sopTypes);
      } catch (err) {
        setError("Failed to fetch SOP types");
        console.error(err);
      }
    };
    fetchSopTypes();
  }, []);

  // Fetch SOP template when type is selected
  useEffect(() => {
    if (sopType) {
      const fetchSopTemplate = async () => {
        try {
          setLoading(true);
          const response = await axios.get(`/sop/${sopType}`);
          setSelectedSop(response.data.sopTemplate);
          initializeForm(response.data.sopTemplate);
        } catch (err) {
          setError("Failed to fetch SOP template");
          console.error(err);
        } finally {
          setLoading(false);
        }
      };
      fetchSopTemplate();
    }
  }, [sopType, initializeForm]);

  // Form submission handler
  const onSubmit = async (data) => {
    try {
      setLoading(true);
      setError(null);

      const submissionData = {
        robotId: data.robotId,
        location: data.location,
        sopType: data.sopType,
        date_time: data.date_time,
        message: data.message,
        sections: {},
      };

      if (data.sections) {
        Object.keys(data.sections).forEach((sectionKey) => {
          const section = data.sections[sectionKey];
          submissionData.sections[sectionKey] = {
            id: section.id,
            taskItems: Object.values(section.taskItems),
          };
        });
      }

      const response = await axios.post(
        "/sop/employee/submit",
        submissionData,
        {
          headers: {
            Authorization: `Bearer ${getAccessToken()}`,
          },
        }
      );

      setSuccess(true);
      console.log("Submission successful:", response.data);
      navigate('/SOP');
    } catch (err) {
      setError(err.response?.data?.error || "Submission failed");
      console.error("Submission error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <IconButton
        onClick={() => navigate(-1)}
        style={{
          position: "absolute",
          top: "20px",
          left: "20px",
          backgroundColor: "white",
          boxShadow: "0px 2px 5px rgba(0,0,0,0.2)",
        }}
      >
        <ArrowBackIcon />
      </IconButton>
      <div
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          padding: "2rem 1rem",
          fontFamily:
            "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif",
          color: "#1a1a1a",
        }}
      >
        {/* Global styles */}
        <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
        * {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }
        body {
          background-color: #f8fafc;
          line-height: 1.6;
        }
        :focus {
          outline: none;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3);
        }
      `}</style>

        {/* Header */}
        <header
          style={{
            marginBottom: "2.5rem",
            textAlign: "center",
          }}
        >
          <h1
            style={{
              fontSize: "2.5rem",
              fontWeight: "700",
              color: "#1e40af",
              marginBottom: "0.5rem",
              background: "linear-gradient(90deg, #1e40af, #3b82f6)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            SOP Submission
          </h1>
          <p
            style={{
              color: "#64748b",
              fontSize: "1.1rem",
              maxWidth: "700px",
              margin: "0 auto",
            }}
          >
            Complete and submit your Standard Operating Procedure documentation
          </p>
        </header>

        {/* Status Messages */}
        {error && (
          <div
            style={{
              backgroundColor: "#fee2e2",
              color: "#b91c1c",
              padding: "1rem",
              borderRadius: "0.5rem",
              marginBottom: "2rem",
              borderLeft: "4px solid #dc2626",
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
            }}
          >
            <svg
              style={{ flexShrink: 0 }}
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div
            style={{
              backgroundColor: "#dcfce7",
              color: "#166534",
              padding: "1rem",
              borderRadius: "0.5rem",
              marginBottom: "2rem",
              borderLeft: "4px solid #16a34a",
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
            }}
          >
            <svg
              style={{ flexShrink: 0 }}
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
            <span>SOP submitted successfully!</span>
          </div>
        )}

        {/* Main Form */}
        <form
          onSubmit={handleSubmit(onSubmit)}
          style={{
            backgroundColor: "white",
            borderRadius: "1rem",
            padding: "2.5rem",
            boxShadow:
              "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
          }}
        >
          {/* Basic Information Section */}
          <section style={{ marginBottom: "3rem" }}>
            <h2
              style={{
                fontSize: "1.5rem",
                fontWeight: "600",
                color: "#1e40af",
                marginBottom: "1.5rem",
                paddingBottom: "0.75rem",
                borderBottom: "1px solid #e2e8f0",
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="8.5" cy="7" r="4"></circle>
                <line x1="20" y1="8" x2="20" y2="14"></line>
                <line x1="23" y1="11" x2="17" y2="11"></line>
              </svg>
              Basic Information
            </h2>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                gap: "1.5rem",
              }}
            >
              {/* SOP Type Field */}
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    fontWeight: "500",
                    color: "#334155",
                  }}
                >
                  SOP Type <span style={{ color: "#dc2626" }}>*</span>
                </label>
                <select
                  style={{
                    width: "100%",
                    padding: "0.75rem 1rem",
                    borderRadius: "0.5rem",
                    border: `1px solid ${
                      errors.sopType ? "#dc2626" : "#cbd5e1"
                    }`,
                    backgroundColor: loading ? "#f1f5f9" : "white",
                    fontSize: "1rem",
                    transition: "all 0.2s ease",
                    cursor: loading ? "not-allowed" : "pointer",
                  }}
                  {...register("sopType", { required: "SOP Type is required" })}
                  disabled={loading}
                >
                  <option value="">Select SOP Type</option>
                  {sopTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.key}
                    </option>
                  ))}
                </select>
                {errors.sopType && (
                  <p
                    style={{
                      marginTop: "0.5rem",
                      color: "#dc2626",
                      fontSize: "0.875rem",
                    }}
                  >
                    {errors.sopType.message}
                  </p>
                )}
              </div>

              {/* Robot ID Field */}
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    fontWeight: "500",
                    color: "#334155",
                  }}
                >
                  Robot ID <span style={{ color: "#dc2626" }}>*</span>
                </label>
                <select
                  style={{
                    width: "100%",
                    padding: "0.75rem 1rem",
                    borderRadius: "0.5rem",
                    border: `1px solid ${
                      errors.robotId ? "#dc2626" : "#cbd5e1"
                    }`,
                    backgroundColor:
                      loading || robotsLoading ? "#f1f5f9" : "white",
                    fontSize: "1rem",
                    transition: "all 0.2s ease",
                    cursor:
                      loading || robotsLoading ? "not-allowed" : "pointer",
                    appearance: "none",
                    backgroundImage:
                      'url(\'data:image/svg+xml;utf8,<svg fill="%2364748b" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg"><path d="M7 10l5 5 5-5z"/></svg>\')',
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 0.75rem center",
                    backgroundSize: "1rem",
                  }}
                  {...register("robotId", { required: "Robot ID is required" })}
                  disabled={loading || robotsLoading}
                >
                  <option value="">
                    {robotsLoading
                      ? "Loading robots..."
                      : robots.length === 0
                      ? "No robots available"
                      : "Select Robot"}
                  </option>
                  {robots.map((robot) => (
                    <option key={robot.robotId} value={robot.robotId}>
                      {robot.robotId}
                    </option>
                  ))}
                </select>
                {errors.robotId && (
                  <p
                    style={{
                      marginTop: "0.5rem",
                      color: "#dc2626",
                      fontSize: "0.875rem",
                    }}
                  >
                    {errors.robotId.message}
                  </p>
                )}
              </div>

              {/* Location Field */}
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    fontWeight: "500",
                    color: "#334155",
                  }}
                >
                  Location <span style={{ color: "#dc2626" }}>*</span>
                </label>
                <input
                  type="text"
                  style={{
                    width: "100%",
                    padding: "0.75rem 1rem",
                    borderRadius: "0.5rem",
                    border: `1px solid ${
                      errors.location ? "#dc2626" : "#cbd5e1"
                    }`,
                    backgroundColor: loading ? "#f1f5f9" : "white",
                    fontSize: "1rem",
                    transition: "all 0.2s ease",
                    cursor: loading ? "not-allowed" : "auto",
                  }}
                  {...register("location", {
                    required: "Location is required",
                  })}
                  placeholder={
                    selectedRobotId
                      ? "Auto-filled from robot (edit if needed)"
                      : "Select a robot or enter location"
                  }
                  disabled={loading || robotsLoading}
                />
                {errors.location && (
                  <p
                    style={{
                      marginTop: "0.5rem",
                      color: "#dc2626",
                      fontSize: "0.875rem",
                    }}
                  >
                    {errors.location.message}
                  </p>
                )}
              </div>

              {/* Date & Time Field */}
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    fontWeight: "500",
                    color: "#334155",
                  }}
                >
                  Date & Time <span style={{ color: "#dc2626" }}>*</span>
                </label>
                <input
                  type="datetime-local"
                  style={{
                    width: "100%",
                    padding: "0.75rem 1rem",
                    borderRadius: "0.5rem",
                    border: `1px solid ${
                      errors.date_time ? "#dc2626" : "#cbd5e1"
                    }`,
                    backgroundColor: loading ? "#f1f5f9" : "white",
                    fontSize: "1rem",
                    transition: "all 0.2s ease",
                    cursor: loading ? "not-allowed" : "auto",
                  }}
                  {...register("date_time", {
                    required: "Date & Time is required",
                  })}
                  disabled={loading}
                />
                {errors.date_time && (
                  <p
                    style={{
                      marginTop: "0.5rem",
                      color: "#dc2626",
                      fontSize: "0.875rem",
                    }}
                  >
                    {errors.date_time.message}
                  </p>
                )}
              </div>
            </div>
          </section>

          {/* SOP Sections */}
          {selectedSop && (
            <section style={{ marginBottom: "3rem" }}>
              <h2
                style={{
                  fontSize: "1.5rem",
                  fontWeight: "600",
                  color: "#1e40af",
                  marginBottom: "1.5rem",
                  paddingBottom: "0.75rem",
                  borderBottom: "1px solid #e2e8f0",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                  <polyline points="10 9 9 9 8 9"></polyline>
                </svg>
                SOP Sections
              </h2>

              {Object.keys(selectedSop.sections).map((sectionKey) => {
                const section = selectedSop.sections[sectionKey];
                return (
                  <div
                    key={sectionKey}
                    style={{
                      backgroundColor: "#f8fafc",
                      borderRadius: "0.75rem",
                      padding: "1.5rem",
                      marginBottom: "1.5rem",
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    <input
                      type="hidden"
                      {...register(`sections.${sectionKey}.id`)}
                      value={section.id}
                    />

                    <h3
                      style={{
                        fontSize: "1.25rem",
                        fontWeight: "600",
                        color: "#1e293b",
                        marginBottom: "1.25rem",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 2h5a2 2 0 0 1 2 2z"></path>
                      </svg>
                      {section.name}
                    </h3>

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "1.25rem",
                      }}
                    >
                      {section.taskItems.map((task) => {
                        const isCompleted =
                          watchedSections?.[sectionKey]?.taskItems?.[task.id]
                            ?.completed;

                        return (
                          <div
                            key={task.id}
                            style={{
                              backgroundColor: "white",
                              borderRadius: "0.5rem",
                              padding: "1.25rem",
                              boxShadow:
                                "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: "1rem",
                                paddingBottom: "0.75rem",
                                borderBottom: "1px solid #e2e8f0",
                              }}
                            >
                              <span
                                style={{
                                  fontWeight: "500",
                                  color: "#1e293b",
                                }}
                              >
                                {task.name}
                              </span>

                              <label
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "0.5rem",
                                  cursor: loading ? "not-allowed" : "pointer",
                                  userSelect: "none",
                                }}
                              >
                                <input
                                  type="checkbox"
                                  style={{
                                    width: "1.25rem",
                                    height: "1.25rem",
                                    cursor: loading ? "not-allowed" : "pointer",
                                    accentColor: "#3b82f6",
                                  }}
                                  {...register(
                                    `sections.${sectionKey}.taskItems.${task.id}.completed`
                                  )}
                                  disabled={loading}
                                />
                                <span
                                  style={{
                                    color: isCompleted ? "#16a34a" : "#64748b",
                                    fontWeight: "500",
                                  }}
                                >
                                  {isCompleted
                                    ? "Completed"
                                    : "Mark as Completed"}
                                </span>
                              </label>
                            </div>

                            {!isCompleted && (
                              <div>
                                <label
                                  style={{
                                    display: "block",
                                    marginBottom: "0.5rem",
                                    fontWeight: "500",
                                    color: "#64748b",
                                  }}
                                >
                                  Comments{" "}
                                  <span style={{ color: "#dc2626" }}>*</span>
                                  <span
                                    style={{
                                      display: "block",
                                      fontSize: "0.875rem",
                                      color: "#94a3b8",
                                      fontWeight: "400",
                                    }}
                                  >
                                    Required if check list is not completed
                                  </span>
                                </label>
                                <textarea
                                  style={{
                                    width: "100%",
                                    minHeight: "100px",
                                    padding: "0.75rem 1rem",
                                    borderRadius: "0.5rem",
                                    border: `1px solid ${
                                      errors.sections?.[sectionKey]
                                        ?.taskItems?.[task.id]?.comment
                                        ? "#dc2626"
                                        : "#cbd5e1"
                                    }`,
                                    backgroundColor: loading
                                      ? "#f1f5f9"
                                      : "white",
                                    fontSize: "1rem",
                                    transition: "all 0.2s ease",
                                    cursor: loading ? "not-allowed" : "auto",
                                    resize: "vertical",
                                  }}
                                  {...register(
                                    `sections.${sectionKey}.taskItems.${task.id}.comment`,
                                    {
                                      validate: (value) => {
                                        return (
                                          isCompleted ||
                                          value?.trim() !== "" ||
                                          "Comment is required when task is not completed"
                                        );
                                      },
                                    }
                                  )}
                                  disabled={loading}
                                />
                                {errors.sections?.[sectionKey]?.taskItems?.[
                                  task.id
                                ]?.comment && (
                                  <p
                                    style={{
                                      marginTop: "0.5rem",
                                      color: "#dc2626",
                                      fontSize: "0.875rem",
                                    }}
                                  >
                                    {
                                      errors.sections[sectionKey].taskItems[
                                        task.id
                                      ].comment.message
                                    }
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </section>
          )}

          {/* Additional Information */}
          <section style={{ marginBottom: "3rem" }}>
            <h2
              style={{
                fontSize: "1.5rem",
                fontWeight: "600",
                color: "#1e40af",
                marginBottom: "1.5rem",
                paddingBottom: "0.75rem",
                borderBottom: "1px solid #e2e8f0",
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
                <polyline points="10 9 9 9 8 9"></polyline>
              </svg>
              Additional Information
            </h2>

            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  fontWeight: "500",
                  color: "#334155",
                }}
              >
                Additional Notes
              </label>
              <textarea
                style={{
                  width: "100%",
                  minHeight: "120px",
                  padding: "0.75rem 1rem",
                  borderRadius: "0.5rem",
                  border: "1px solid #cbd5e1",
                  backgroundColor: loading ? "#f1f5f9" : "white",
                  fontSize: "1rem",
                  transition: "all 0.2s ease",
                  cursor: loading ? "not-allowed" : "auto",
                  resize: "vertical",
                }}
                {...register("message")}
                disabled={loading}
                placeholder="Enter any additional notes or comments here..."
              />
            </div>
          </section>

          {/* Submit Button */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <button
              type="submit"
              style={{
                backgroundColor: loading ? "#94a3b8" : "#3b82f6",
                color: "white",
                padding: "0.875rem 2rem",
                borderRadius: "0.5rem",
                border: "none",
                fontSize: "1rem",
                fontWeight: "600",
                cursor: loading ? "not-allowed" : "pointer",
                transition: "all 0.2s ease",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                boxShadow:
                  "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                ":hover": {
                  backgroundColor: loading ? "#94a3b8" : "#2563eb",
                },
              }}
              disabled={loading}
            >
              {loading ? (
                <>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ animation: "spin 1s linear infinite" }}
                  >
                    <path d="M21 12a9 9 0 1 1-6.219-8.56"></path>
                  </svg>
                  Processing...
                </>
              ) : (
                <>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                  </svg>
                  Submit SOP
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SOPSubmissionForm;
